// GitHub branch — OAuth connect. Mirrors GoogleConnectStep: open the OAuth popup, then
// advance once the github integration shows up in readiness (the popup's cross-origin
// postMessage is best-effort, so the readiness poll is the reliable completion signal).
// No extension. Note: GitHub captures nothing until repositories are selected — we say so
// and point at the Sources page, but connecting is enough to satisfy onboarding.

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';
import { useOnboardingReadiness } from '../../hooks/useOnboardingReadiness';
import { SourceIcon } from '../shared/SourceIcon';

export function GitHubConnectStep({ onConnected, onBack }: { onConnected: () => void; onBack: () => void }) {
  const { open } = useOAuthPopup();
  const { data, refetch } = useOnboardingReadiness({ pollMs: 3000 });
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (data?.connectedProviders.includes('github') && !fired.current) {
      fired.current = true;
      onConnected();
    }
  }, [data?.connectedProviders, onConnected]);

  const connect = () => {
    setError(null);
    setStarted(true);
    open(
      async () => {
        const { url } = await apiFetch<{ url: string }>(
          '/api/surfaces/sources/github-connect-url',
          { credentials: 'include' }
        );
        return url;
      },
      (payload) => {
        if (!payload.ok) {
          setError(payload.error || 'Failed to connect GitHub');
          setStarted(false);
        } else {
          void refetch();
        }
      }
    );
  };

  return (
    <>
      <div className="crumb">STEP 02 · GITHUB</div>
      <div className="wh">Connect <span className="em">GitHub</span>.</div>
      <p className="wp">
        Read-only access to your merged pull requests. After connecting, pick which repos
        Terum reads on the Sources page - nothing syncs until you do.
      </p>
      <div style={{ marginTop: 22, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={connect}>
          <span className="ltile" style={{ width: 18, height: 18 }}><SourceIcon id="github" size={18} /></span>
          Authorize with GitHub
        </button>
        {error ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--rose)' }}>⚠ {error}</span>
        ) : started ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--inkS)' }}>
            <span className="dot acc pulse" /> Authorize in the popup…
          </span>
        ) : null}
      </div>
      <div className="wiz-foot">
        <button className="btn btn-secondary" onClick={onBack}>← Back to sources</button>
        <span className="prog">Step 2 · GitHub</span>
      </div>
    </>
  );
}
